import { Stack, Typography } from '@mui/material';
import {
  AppRoutes,
  generateStaticId,
  NotificationDisplayType,
  NotificationKeys,
  NotificationType,
  orderApprovalSlice,
  OrderApprovalStatusType,
  rejectOrderApproval,
  resetActiveOrder,
  resetOrderCart,
  RouteName,
  selectOrdersByApprovalStatus,
  useAppDispatch,
  useAppSelector,
} from 'common';
import { validateStringEmptyOrMaxLength } from 'common/src/helpers/validation/general';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { CfDialog } from '../../../../cf-ui/dialogs/Dialog/CfDialog';
import { CfTextField } from '../../../../cf-ui/TextField/CfTextField';
import { getResolution } from '../../../../utilities/resolution';

type RejectOrderDialogProps = {
  orderEntryHeaderId: string;
  onClose: () => void;
};

function RejectOrderDialog(props: RejectOrderDialogProps): JSX.Element {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const orders = useAppSelector((s) => selectOrdersByApprovalStatus(s, OrderApprovalStatusType.ReadyForReview));
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionErrorMessages, setRejectionErrorMessages] = useState<string[]>([]);

  const onSubmit = async () => {
    const isSuccess = await dispatch(
      rejectOrderApproval(props.orderEntryHeaderId, getResolution(), rejectionReason.trim())
    );

    if (isSuccess) {
      if (orders.length > 1) {
        dispatch(orderApprovalSlice.actions.setHasShownDialogOnHome(false));
      }
      dispatch(resetActiveOrder());
      dispatch(resetOrderCart());
      history.push(AppRoutes[RouteName.Home].Path, {
        notification: {
          Id: generateStaticId('toastNotification'),
          NotificationType: NotificationType.Success,
          NotificationDisplayType: NotificationDisplayType.Toast,
          AutoDismiss: 0,
          CanUserDismiss: true,
          Key: NotificationKeys.Toast,
          Icon: 'fa-check-circle',
          Message: 'Order successfully rejected',
        },
      });
    }
  };

  return (
    <CfDialog
      isOpen
      title='Reject order'
      onClose={props.onClose}
      primaryButtonProps={{
        disabled: rejectionErrorMessages.length > 0,
        onClick: onSubmit,
        children: 'Reject order',
      }}
      secondaryButtonProps={{
        onClick: props.onClose,
        children: 'Cancel',
      }}
    >
      <Stack gap={2}>
        <Typography variant='paragraph'>
          This order will be rejected and sent back to the submitter. Below you can enter a note to explain why the
          order was rejected.
        </Typography>
        <CfTextField
          inputLabel='Rejection reason (optional)'
          error={rejectionErrorMessages.length > 0}
          helpLabel={rejectionErrorMessages.length ? rejectionErrorMessages.join(' ') : undefined}
          value={rejectionReason}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
            setRejectionReason(event.target.value);
            const validationMessages = validateStringEmptyOrMaxLength(
              'Rejection reason',
              event.target.value,
              100,
              true
            );
            if (validationMessages.length === 0) setRejectionErrorMessages([]);
            else setRejectionErrorMessages(validationMessages);
          }}
        />
      </Stack>
    </CfDialog>
  );
}

export default RejectOrderDialog;
